import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Switch from "@mui/material/Switch";
import { TitleWithNav } from "common/TitleWithNav";
import Modal from "common/Modal";
import CollapsablePanel from "common/layouts/CollapsablePanel";
import NumberInputWithSpinner from "common/NumberInputWithSpinnerState";
import { checkNothingPending, safeStr } from "common/api";
import { isPolicyNameValid } from "../Rules/utils";

const Wrapper = styled.div`
  .sw-input.MuiSwitch-root {
    & .Mui-checked {
      color: var(--switch-checked-green);
    }

    & .Mui-checked + .MuiSwitch-track {
      background-color: var(--switch-checked-light-green);
    }
  }
`;

const ChangesWrapper = styled.div`
  padding: 0;
`;

const PendingChanges = styled.div`
  display: flex;
  margin-top: 7px;
`;

const Circle = styled.div`
  height: 16px;
  width: 16px;
  background: #f44336;
  border-radius: 50%;
  display: inline-block;
  margin-right: 5px;
`;

export function parsePolicyMonitor(response) {
  const lines = response.trim("\n").split("\n");
  return lines.reduce((acc, line) => {
    const [key, value] = line.trim("\n").split(":");
    if (key === "Name") {
      acc = value.trim("\n");
    }
    return acc;
  }, "");
}

const parsePolicies = (response) => {
  const [head, ...rows] = response.trim("\n").split("\n");
  const headAttrs = head.split(/\s+/);
  const nameIndex = headAttrs.findIndex((el) => el === "NAME");
  const udrIndex = headAttrs.findIndex((el) => el === "UDR");
  return rows.reduce((acc, row) => {
    const attrs = row.split(/\s+/);
    acc[attrs[nameIndex]] = { name: attrs[nameIndex], udr: attrs[udrIndex] };
    return acc;
  }, {});
};

export const getPolicies = () =>
  ifCl.run("show policy monitor").then(parsePolicies);

const DEFAULT_POLICY = {
  name: "",
  udr: 0,
  udrEnabled: true,
};

const geUdr = (value) => {
  if (value.endsWith("%")) {
    return Number(value.slice(0, value.length - 1));
  }
  return 0;
};

const PolicyMonitorEdit = ({ returnView, policy, tab }) => {
  const [udrEnabled, setUdrEnabled] = useState(DEFAULT_POLICY.udrEnabled);
  const [name, setName] = useState(DEFAULT_POLICY.name);
  const [udr, setUdr] = useState(DEFAULT_POLICY.udr);
  const [policies, setPolicies] = useState({});
  const [initPolicy, setInitPolicy] = useState(DEFAULT_POLICY);

  useEffect(() => {
    getPolicies().then((response) => {
      setPolicies(response);
      if (policy) {
        const editPolicy = response[policy];
        if (!editPolicy) {
          showModalError("Error:", `Unknow policy ${policy}`);
        } else {
          const parsedEditPolicy = {
            name: editPolicy.name,
            udrEnabled: editPolicy.udr === "auto" ? true : false,
            udr: geUdr(editPolicy.udr),
          };
          setInitPolicy(parsedEditPolicy);
          setName(parsedEditPolicy.name);
          setUdr(parsedEditPolicy.udr);
          setUdrEnabled(parsedEditPolicy.udrEnabled);
        }
      }
    });
  }, []);

 

  const handleUdr = () => {
    setUdrEnabled(!udrEnabled);
  };

  const changes =
    udrEnabled !== initPolicy.udrEnabled ||
    name !== initPolicy.name ||
    udr !== initPolicy.udr;

  const validatePolicy = (policy) => {
    if (!isPolicyNameValid(policy.name)) {
      showModalError("Warning:", "Invalid name");
      return false;
    }

    return true;
  };

  const handleApply = () => {
    let cmdClear;
    const newPolicy = {
      name,
      udr,
    };
    if (!validatePolicy(newPolicy)) return;
    if (policy) {
      if (policy !== newPolicy.name) {
        const policiesSet = new Set(Object.keys(policies));
        if (policiesSet.has(newPolicy.name)) {
          showModalError("Error:", "Duplicated policy name");
          return;
        }
        cmdClear = `clear policy monitor ${safeStr(policy)}`;
      }
    }

    const cmdPart1 = `configure\n policy monitor ${safeStr(
      newPolicy.name
    )}\n sdr\n`;
    const cmdPart2 = udrEnabled
      ? ` udr-auto\n commit\n`
      : ` no udr-auto\n udr ${newPolicy.udr}\n commit\n`;

    checkNothingPending()
      .then(() => {
        if (cmdClear) {
          return ifCl.run(cmdClear);
        } else {
          return;
        }
      })
      .then(() => ifCl.run(`${cmdPart1} ${cmdPart2}`, true))
      .then((response) => {
        globalNavigate(returnView, { tab });
      })
      .catch((error) => {
        const updateNameError = error.match(/%ERR-ENOENT: Cannot remove policy "(.*)" because it is part of a rule/)
        if(updateNameError){
          showModalError("Error:", "Cannot update name policy because it is part of a rule.");
        } else {
          showModalError("Error:", error);
        }
      });
  };

  return (
    <Wrapper>
      <div className="row clearfix">
        <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
          <div className="card container-lg">
            <div className="header block-header">
              <TitleWithNav returnView={returnView}>
                <div>
                  <h2>{`${
                    policy ? "Edit" : "New"
                  } Subscriber Monitoring Policy`}</h2>
                </div>
              </TitleWithNav>
            </div>
            <div className="body table-responsive padding-b-0">
              <div className="row margin-t-10">
                <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-b-0">
                  <div className="input-group">
                    <span className="input-group-addon" title="Policy name">
                      Name
                    </span>
                    <input
                      id="poliMonitorName"
                      type="text"
                      size="55"
                      style={{ borderBottom: "1px solid #ddd" }}
                      className="align-left"
                      onChange={({ target }) => setName(target.value)}
                      value={name}
                    />
                  </div>
                </div>
                <div className="margin-0 padding-0">
                  <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0">
                    <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0 f-dpi-per-user">
                      <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                        <div
                          className="input-group"
                          style={{ paddingTop: "5px" }}
                        >
                          <span
                            className="input-group-addon align-left"
                            title="Block traffic"
                          >
                            Automatic UDR rate:{" "}
                            <span id="statusBlock" className="font-bold">
                              {udrEnabled ? "enabled" : "disabled"}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                        <Switch
                          className="sw-input"
                          checked={udrEnabled}
                          onChange={handleUdr}
                        />
                      </div>
                      <div className="col-lg-1 col-md-1 col-sm-0 col-xs-0 margin-0 padding-0"></div>
                    </div>
                  </div>
                </div>
                {udrEnabled ? null : (
                  <Wrapper className="margin-0 padding-0">
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0">
                      {/* <!-- ************ Column A ************ --> */}
                      <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                        <NumberInputWithSpinner
                          name="poliMonitorUdr"
                          label="UDR generation percentage"
                          min={0}
                          max={100}
                          initialValue={udr}
                          formatInput={(d) => d3.format(".2f")(d)}
                          step={0.01}
                          onChange={(value) => setUdr(value)}
                          onReset={() => {
                            setEditedPolicy({
                              ...editedPolicy,
                              ["udr"]: DEFAULT_POLICY.udr,
                            });
                          }}
                        />
                      </div>
                      {/* <!-- ************ Column B ************ --> */}
                      <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0"></div>
                      <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0 margin-t-15">
                        <p>
                          Warning: setting{" "}
                          <code>UDR generation percentage</code> to a high
                          percentage will degrade performance.
                        </p>
                      </div>
                    </div>
                  </Wrapper>
                )}
              </div>
              <ChangesWrapper>
                <div className="col-lg-6 col-md-6 col-sm-6 col-xs-6 margin-b-0 padding-0">
                  {changes ? (
                    <PendingChanges>
                      <Circle />
                      <b>Pending Changes</b>
                    </PendingChanges>
                  ) : null}
                </div>
                <div className="col-lg-6 col-md-6 col-sm-6 col-xs-6 margin-b-0 align-right padding-0">
                  <div className="align-right margin-b-0">
                    <button
                      type="button"
                      className="btn btn-link waves-effect hidden-to-operators"
                      disabled={changes === false}
                      onClick={handleApply}
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      className="btn btn-link waves-effect"
                      onClick={() => globalNavigateBack()}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </ChangesWrapper>
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default PolicyMonitorEdit;
